import { useEffect, useState } from 'react';
import { X, ShieldAlert, Unlock, Loader2, AlertTriangle } from 'lucide-react';

/* ================================================================== */
/*  Block / Unblock member modal                                      */
/*                                                                    */
/*  PUT /api/v1/agency/members/{user_id}/block                        */
/*       body { reason }  — required, not blank. Takes effect on the  */
/*       admin's very next request.                                   */
/*                                                                    */
/*  PUT /api/v1/agency/members/{user_id}/unblock                      */
/*       body { reason? } — optional. Access is back immediately.     */
/*                                                                    */
/*  Both are PUT: there is no reactivate_invitation permission, so    */
/*  restoring access goes through /unblock rather than a re-invite.   */
/*                                                                    */
/*  Props:                                                            */
/*    open / onClose / onSubmit(reason) / busy / error                */
/*    mode  'block' | 'unblock'                                       */
/*    member  mapped row (user_id, name, email, state…) or null       */
/* ================================================================== */

export default function BlockMemberModal({
  open,
  mode = 'block',
  member,
  busy = false,
  error = '',
  onClose,
  onSubmit,
}) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const isBlock = mode === 'block';
  const reasonRequired = isBlock; // unblock's body is optional
  const reasonBlank = reason.trim().length === 0;
  const canSubmit = busy || (reasonRequired ? !reasonBlank : true);

  /* Fresh state every time the modal opens for a member */
  useEffect(() => {
    if (open) {
      setReason('');
      setTouched(false);
    }
  }, [open, member?.user_id]);

  if (!open || !member) return null;

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit(reason.trim());
  };

  const initials =
    (
      (member.raw?.first_name?.[0] || '') + (member.raw?.last_name?.[0] || '')
    ).toUpperCase() || '??';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-sm rounded-2xl bg-white shadow-lift ring-1 ring-slate-100 animate-modal-in">
        {/* Header */}
        <div
          className={`relative overflow-hidden rounded-t-2xl p-5 ${
            isBlock
              ? 'bg-gradient-to-r from-[#7F1D1D] via-[#DC2626] to-[#F87171]'
              : 'bg-gradient-to-r from-[#064E3B] via-[#059669] to-[#34D399]'
          }`}
        >
          <div className="absolute -top-12 right-4 w-36 h-36 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center shrink-0">
                {isBlock ? (
                  <ShieldAlert size={18} className="text-white" />
                ) : (
                  <Unlock size={18} className="text-white" />
                )}
              </span>
              <div>
                <h2 className="text-base font-bold text-white">
                  {isBlock ? 'Block member' : 'Unblock member'}
                </h2>
                <p className="text-[11px] text-white/75">
                  {isBlock
                    ? 'Takes effect on their very next request'
                    : 'Access is back immediately'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              title="Close"
              className="w-8 h-8 rounded-lg text-white/70 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <form onSubmit={submit} noValidate>
          <div className="p-5 space-y-3.5">
            {/* API error (e.g. 403 owner guard, 422 blank reason) */}
            {error && (
              <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5">
                <AlertTriangle
                  size={14}
                  className="mt-0.5 shrink-0 text-red-500"
                />
                <p className="text-[12px] font-semibold text-red-600">
                  {error}
                </p>
              </div>
            )}

            {/* Member chip */}
            <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
              <span className="w-9 h-9 rounded-full bg-white ring-1 ring-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-500 shrink-0">
                {initials}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">
                  {member.name}
                </p>
                <p className="truncate text-[11px] text-slate-400">
                  {member.email}
                </p>
              </div>
            </div>

            {/* Reason */}
            <label className="block">
              <span className="mb-1 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                Reason {isBlock && <span className="text-red-500">*</span>}
                {!isBlock && (
                  <span className="font-semibold normal-case text-slate-400">
                    (optional)
                  </span>
                )}
              </span>
              {isBlock ? (
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  onBlur={() => setTouched(true)}
                  rows={3}
                  maxLength={280}
                  placeholder="e.g. Left the company"
                  className={`w-full rounded-xl border bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-300 shadow-sm focus:outline-none focus:ring-2 transition ${
                    touched && reasonBlank
                      ? 'border-red-300 focus:ring-red-200'
                      : 'border-slate-200 focus:ring-brand/40 focus:border-brand'
                  }`}
                />
              ) : (
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={280}
                  placeholder="e.g. Cleared after review"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 placeholder:text-slate-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition"
                />
              )}
              {touched && reasonBlank && isBlock && (
                <span className="mt-1 block text-[11px] font-semibold text-red-500">
                  A reason is required and can’t be blank.
                </span>
              )}
              <span className="mt-1 block text-[10px] text-slate-400">
                {isBlock
                  ? 'The reason is stored on the member (block_reason) and shown in the list.'
                  : 'Their status returns to what it was before the block.'}
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 border-t border-slate-100 p-4">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-xl text-sm font-semibold text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-bold text-white shadow-md transition-all active:scale-[0.98] disabled:opacity-50 ${
                isBlock
                  ? 'bg-gradient-to-r from-red-500 to-red-600 shadow-red-500/25 hover:opacity-95'
                  : 'bg-gradient-to-r from-emerald-500 to-emerald-600 shadow-emerald-500/25 hover:opacity-95'
              }`}
            >
              {busy ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  {isBlock ? 'Blocking…' : 'Unblocking…'}
                </>
              ) : (
                <>
                  {isBlock ? <ShieldAlert size={15} /> : <Unlock size={15} />}
                  {isBlock ? 'Block member' : 'Unblock member'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
